import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { ArrangementType } from '@pams/shared';
import {
  EMPTY_ARRANGEMENT_LIST_FILTERS,
  LIST_FILTERS_STORAGE_PREFIX,
  activeFilterCount,
  canonicalListFilters,
  listFiltersStorageKey,
  parseStoredListFilters,
  serialiseListFilters,
  type ArrangementListFilters,
} from '@/lib/arrangementListFilters';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] THE ONE STORE BEHIND "ADJUST FILTERS" — PER OFFICER, EVERY TAB, UNTIL SIGN-OUT.
 *
 *  ⚠️ A BROWSER STORE, NOT THE URL — THE USER'S DECISION, AND THE REVERSE OF
 *  `useColumnVisibility`'s "THE URL WINS". The filter must follow the operator across BOTH order
 *  lists (Manage Arrangements and Durga Puja PA), across reloads, and into every open tab, until
 *  they sign out. URL state is per page and per tab, so it can do none of that — and Back would
 *  resurrect an older selection. The cost, stated: a shared link and `?from=` do not carry the
 *  filter; the store re-applies it on return, and the amber button says it is on.
 *
 *  ⚠️ PER OFFICER: `pams.arrangement-filters.<officerId>`. A tab still showing officer X after Y
 *  has signed in elsewhere can then never overwrite Y's filter. `AuthProvider` sweeps the whole
 *  PREFIX in `reset` and `signIn` (`clearArrangementListFilters`), so nothing outlives a session.
 *
 *  ⚠️ READ-THROUGH, NEVER A MIRROR FILLED ONCE — [C1a]'s lesson. `getSnapshot` re-reads storage on
 *  EVERY call. A copy taken at mount goes stale in exactly the cases this exists for: another tab's
 *  write, a page restored from the back-forward cache, a test's `localStorage.clear()` — none of
 *  which this tab is told about through any path it is not already listening on. `getItem` is
 *  cheap; a stale filter is a list silently narrower than the operator thinks it is.
 *
 *  ⚠️ AND A STABLE REFERENCE, OR REACT LOOPS. `useSyncExternalStore` compares snapshots with
 *  `Object.is` and re-renders on any difference, so the parse is memoised by the RAW string: the
 *  same bytes are the same object, and every empty answer is THE frozen empty value
 *  (`lib/arrangementListFilters.ts`). A fresh object per read is an infinite render, which React
 *  reports as "The result of getSnapshot should be cached" — the test that pins this listens for it.
 *
 *  ⚠️ IT WRITES, SO IT LIVES IN `hooks/`. The `src/lib/**` lint block bans storage writes because
 *  that rule is about CREDENTIALS, and its own message sends a preference to a hook;
 *  `lib/eslintConfig.test.ts` E3 asserts this file keeps the right to write and E4 that the pure
 *  half in `lib/` never names a storage global.
 *
 *  ⚠️ NO EFFECTS. A module-level `subscribe`/`getSnapshot` pair (`pwa/src/lib/dutyEvents.ts`'s
 *  shape): the hook body only reads, so StrictMode's double render and double subscribe are both
 *  harmless, and the react-hooks compiler rules have nothing to object to.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ A THUNK, CALLED INSIDE `try`. `tokenStore.ts` records the live bug this avoids: in a sandboxed
 * frame the property access ITSELF throws, so naming the global outside the `try` fails in exactly
 * the case the `try` was written for.
 */
const area = (): Storage => window.localStorage;

/**
 * ⚠️ WHAT THIS TAB HOLDS BECAUSE A WRITE DID NOT LAND — and only that. Safari's private mode lets
 * `getItem` succeed while `setItem` throws, and a full or sandboxed area can accept a write and
 * drop it; without this, Apply would visibly do nothing, because the next read-through would hand
 * back the old value. `null` is a held CLEAR. A key absent from this map means storage is the
 * truth, which is the ordinary case.
 */
const unwritten = new Map<string, string | null>();

/** The last parse per key, keyed by the raw string it came from — the stable-reference rule above. */
const parsed = new Map<string, { raw: string | null; value: ArrangementListFilters }>();

const listeners = new Set<() => void>();

/** A tab never receives its own `storage` event, so every write and clear tells this tab's readers itself. */
const emit = (): void => {
  for (const listener of [...listeners]) listener();
};

const readRaw = (key: string): string | null => {
  if (unwritten.has(key)) return unwritten.get(key) ?? null;
  try {
    return area().getItem(key);
  } catch {
    /* Unreadable storage holds nothing this tab can show; the list is simply unfiltered. */
    return null;
  }
};

/**
 * Writes, then READS BACK — `tokenStore.ts`'s `writeEntry` rule and `locationShareStore.ts`'s:
 * only a value that is retrievable afterwards counts as stored. `null` removes the key.
 */
const writeRaw = (key: string, raw: string | null): void => {
  try {
    const storage = area();
    if (raw === null) storage.removeItem(key);
    else storage.setItem(key, raw);
    if (storage.getItem(key) === raw) unwritten.delete(key);
    else unwritten.set(key, raw);
  } catch {
    unwritten.set(key, raw);
  }
  emit();
};

const snapshotFor = (officerId: string | undefined): ArrangementListFilters => {
  if (officerId === undefined) return EMPTY_ARRANGEMENT_LIST_FILTERS;
  const key = listFiltersStorageKey(officerId);
  const raw = readRaw(key);
  const cached = parsed.get(key);
  if (cached && cached.raw === raw) return cached.value;
  const value = parseStoredListFilters(raw);
  parsed.set(key, { raw, value });
  return value;
};

/**
 * One subscription per reader: this tab's own writes, another tab's (`storage`), and a page coming
 * back from the back-forward cache (`pageshow`), which missed every event while it was frozen.
 *
 * ⚠️ `key === null` IS ANOTHER TAB'S `clear()`, and it is ours too — a sign-out elsewhere that
 * swept storage wholesale must reach this tab. ⚠️ AND ANOTHER TAB'S WRITE SUPERSEDES A VALUE THIS
 * TAB ONLY HELD IN MEMORY: that write landed, so it is the newer truth, and the last write wins
 * wherever it was made.
 */
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  const onStorage = (event: StorageEvent): void => {
    if (event.key === null) {
      unwritten.clear();
      listener();
    } else if (event.key.startsWith(LIST_FILTERS_STORAGE_PREFIX)) {
      unwritten.delete(event.key);
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('pageshow', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('pageshow', listener);
  };
};

export type ArrangementListFiltersStore = {
  /** Stable reference until the stored value changes. */
  readonly filters: ArrangementListFilters;
  /** What THIS list honours — on a typed list, the divisions and [DP2026] the booklet. */
  readonly active: boolean;
  readonly activeCount: number;
  /** Whether any division is chosen — the one filter both lists apply. */
  readonly divisionsActive: boolean;
  /**
   * Canonicalises; an empty value REMOVES the key. Does nothing without an officer. ⚠️ [TYPE-FILTER] On a typed list
   * it writes the divisions and [DP2026] the booklet, over the stored type; on Manage Arrangements, over the stored booklet.
   */
  readonly apply: (next: ArrangementListFilters) => void;
  /**
   * Clears what THIS list shows. ⚠️ [TYPE-FILTER] On a typed list that is the divisions and [DP2026] the booklet: the
   * "Order type" belongs to Manage Arrangements, is not shown there, and must not be wiped from there — and the booklet
   * is the typed list's, so Manage Arrangements' Clear keeps it.
   */
  readonly clear: () => void;
};

/**
 * ⚠️ THE OFFICER IS A PARAMETER, NOT A `useAuth()` INSIDE. The per-officer contract is then visible
 * at the call site, and no consumer can read one officer's filter while writing another's. With no
 * officer the store reads empty and writes nothing.
 *
 * ⚠️ [TYPE-FILTER] AND THE LIST IS ONE TOO: `fixedType` for the typed list, absent for Manage
 * Arrangements. (`AuthProvider` never reads the store; it only clears it — `clearArrangementListFilters`.)
 */
export const useArrangementListFilters = (
  officerId: string | undefined,
  fixedType?: ArrangementType,
): ArrangementListFiltersStore => {
  const getSnapshot = useCallback(() => snapshotFor(officerId), [officerId]);
  const filters = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const apply = useCallback(
    (next: ArrangementListFilters) => {
      if (officerId === undefined) return;
      /*
       * ⚠️ [TYPE-FILTER] ON A TYPED LIST ONLY THE DIVISIONS ARE THIS LIST'S, so they are written over the STORED
       * type, read through at the call — `clear`'s rule. A value handed in may carry a type from before another
       * tab moved it, and this list neither shows nor honours one.
       */
      /*
       * ⚠️ [DP2026] AND THE REVERSE FOR THE BOOKLET: it is the typed list's own filter, so Manage Arrangements
       * writes its divisions and type over the STORED booklet, and a typed list writes its divisions and booklet.
       */
      const stored = snapshotFor(officerId);
      const written =
        fixedType === undefined
          ? { ...next, booklet: stored.booklet }
          : { ...stored, divisionOrgUnitIds: next.divisionOrgUnitIds, booklet: next.booklet };
      writeRaw(listFiltersStorageKey(officerId), serialiseListFilters(canonicalListFilters(written)));
    },
    [officerId, fixedType],
  );

  const clear = useCallback(() => {
    if (officerId === undefined) return;
    const key = listFiltersStorageKey(officerId);
    /* Read through at the call, never a closed-over snapshot that another tab may have moved on from. */
    /* [DP2026] Each list clears the filters it honours: the typed list keeps the stored type, Manage Arrangements the booklet. */
    const stored = snapshotFor(officerId);
    writeRaw(
      key,
      serialiseListFilters(
        canonicalListFilters(
          fixedType === undefined
            ? { divisionOrgUnitIds: [], type: EMPTY_ARRANGEMENT_LIST_FILTERS.type, booklet: stored.booklet }
            : { ...stored, divisionOrgUnitIds: [], booklet: 'all' },
        ),
      ),
    );
  }, [officerId, fixedType]);

  return useMemo(() => {
    const activeCount = activeFilterCount(filters, fixedType);
    return {
      filters,
      active: activeCount > 0,
      activeCount,
      divisionsActive: filters.divisionOrgUnitIds.length > 0,
      apply,
      clear,
    };
  }, [filters, fixedType, apply, clear]);
};

/**
 * ⚠️ EVERY OFFICER'S FILTER, GONE — `AuthProvider`'s call, in `reset` and `signIn`, and the tests'
 * reset. Sweeps the PREFIX rather than one key: the session ending is not one officer's business,
 * and a key left by an earlier officer on a shared browser must not reappear for anybody.
 *
 * ⚠️ A REMOVAL THAT DOES NOT LAND IS HELD AS A CLEAR FOR THIS TAB, by the write rule above, so a
 * sign-out on broken storage still shows an unfiltered list here.
 */
export const clearArrangementListFilters = (): void => {
  unwritten.clear();
  try {
    const storage = area();
    const keys = Array.from({ length: storage.length }, (_unused, index) => storage.key(index)).filter(
      (key): key is string => key !== null && key.startsWith(LIST_FILTERS_STORAGE_PREFIX),
    );
    for (const key of keys) {
      try {
        storage.removeItem(key);
        if (storage.getItem(key) !== null) unwritten.set(key, null);
      } catch {
        unwritten.set(key, null);
      }
    }
  } catch {
    /* Storage that cannot even be listed holds nothing this tab could have read. */
  }
  emit();
};
